import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Dimensions,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';

interface PulseOnboardingOverlayProps {
  visible: boolean;
  onClose: () => void;
}

/**
 * First-time onboarding overlay for Pulse tab
 * Explains Trinity boxes (YOUR GROUP | MARKET | YOUR EDGE)
 * Shows only once per user (tracked in AsyncStorage)
 */
export const PulseOnboardingOverlay: React.FC<PulseOnboardingOverlayProps> = ({
  visible,
  onClose,
}) => {
  console.log('[PulseOnboarding] Rendering overlay, visible:', visible);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View style={styles.backdrop}>
        <View style={styles.container}>
          <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
            {/* Header */}
            <View style={styles.header}>
              <View style={styles.iconContainer}>
                <Ionicons name="pulse" size={32} color={Colors.ai.primary} />
              </View>
              <Text style={styles.title}>Welcome to Pulse!</Text>
              <Text style={styles.subtitle}>See how your group compares to the overall market</Text>
            </View>

            {/* Trinity Boxes Explanation */}
            <View style={styles.section}>
              <View style={styles.boxExample}>
                <View style={[styles.exampleBox, styles.groupBoxExample]}>
                  <Text style={styles.exampleLabel}>YOUR GROUP</Text>
                  <Text style={styles.exampleNumber}>68%</Text>
                  <Text style={styles.exampleMeta}>📈 Bullish</Text>
                </View>
              </View>
              <Text style={styles.boxTitle}>YOUR GROUP</Text>
              <Text style={styles.boxDescription}>
                Your trading community's sentiment based on recent messages. Higher = more bullish,
                lower = more bearish.
              </Text>
            </View>

            <View style={styles.section}>
              <View style={styles.boxExample}>
                <View style={[styles.exampleBox, styles.marketBoxExample]}>
                  <Text style={styles.exampleLabel}>MARKET</Text>
                  <Text style={styles.exampleNumber}>52%</Text>
                  <Text style={styles.exampleMeta}>→ Neutral</Text>
                </View>
              </View>
              <Text style={styles.boxTitle}>MARKET</Text>
              <Text style={styles.boxDescription}>
                Overall market sentiment from Fear & Greed Index. This is your benchmark to compare
                against.
              </Text>
            </View>

            <View style={styles.section}>
              <View style={styles.boxExample}>
                <View style={[styles.exampleBox, styles.edgeBoxExample]}>
                  <Text style={styles.exampleLabel}>YOUR EDGE</Text>
                  <Text style={styles.exampleNumber}>+16%</Text>
                  <Text style={styles.exampleMeta}>⚡ Ahead</Text>
                </View>
              </View>
              <Text style={styles.boxTitle}>YOUR EDGE</Text>
              <Text style={styles.boxDescription}>
                The difference between your group and the market. Positive = more bullish, negative
                = more bearish (contrarian).
              </Text>
            </View>

            {/* Key Insight */}
            <View style={styles.insightBox}>
              <Ionicons name="bulb" size={20} color={Colors.status.warning} />
              <Text style={styles.insightText}>
                <Text style={styles.insightBold}>Track your edge daily</Text> to see if your group's
                predictions pay off over time!
              </Text>
            </View>

            {/* CTA Button */}
            <TouchableOpacity style={styles.ctaButton} onPress={onClose} activeOpacity={0.8}>
              <Text style={styles.ctaButtonText}>Got It!</Text>
              <Ionicons name="checkmark-circle" size={20} color={Colors.text.primary} />
            </TouchableOpacity>

            {/* Skip Link */}
            <TouchableOpacity style={styles.skipButton} onPress={onClose}>
              <Text style={styles.skipButtonText}>Skip tutorial</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const { width } = Dimensions.get('window');
const MODAL_WIDTH = Math.min(width - 48, 400);

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  container: {
    width: MODAL_WIDTH,
    maxHeight: '85%',
    backgroundColor: Colors.background.secondary,
    borderRadius: 16,
  },
  content: {
    padding: 24,
    flexGrow: 1,
  },
  header: {
    alignItems: 'center',
    marginBottom: 24,
  },
  iconContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: `${Colors.ai.primary}20`,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: '900',
    color: Colors.text.primary,
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 14,
    color: Colors.text.secondary,
    textAlign: 'center',
    lineHeight: 20,
  },

  // Section
  section: {
    marginBottom: 24,
  },
  boxExample: {
    alignItems: 'center',
    marginBottom: 12,
  },
  exampleBox: {
    width: 110,
    height: 80,
    borderRadius: 8,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  groupBoxExample: {
    backgroundColor: `${Colors.market.profit}15`,
    borderColor: Colors.market.profit,
  },
  marketBoxExample: {
    backgroundColor: `${Colors.text.tertiary}15`,
    borderColor: Colors.text.tertiary,
  },
  edgeBoxExample: {
    backgroundColor: `${Colors.ai.primary}15`,
    borderColor: Colors.ai.primary,
  },
  exampleLabel: {
    fontSize: 8,
    fontWeight: '700',
    color: Colors.text.tertiary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  exampleNumber: {
    fontSize: 22,
    fontWeight: '900',
    color: Colors.text.primary,
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  exampleMeta: {
    fontSize: 8,
    fontWeight: '600',
    color: Colors.text.secondary,
  },
  boxTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: Colors.text.primary,
    marginBottom: 6,
    textAlign: 'center',
  },
  boxDescription: {
    fontSize: 13,
    color: Colors.text.secondary,
    textAlign: 'center',
    lineHeight: 18,
  },

  // Insight Box
  insightBox: {
    flexDirection: 'row',
    backgroundColor: `${Colors.status.warning}15`,
    borderRadius: 8,
    padding: 12,
    marginBottom: 24,
    gap: 10,
    alignItems: 'flex-start',
  },
  insightText: {
    flex: 1,
    fontSize: 13,
    color: Colors.text.secondary,
    lineHeight: 18,
  },
  insightBold: {
    fontWeight: '700',
    color: Colors.text.primary,
  },

  // CTA Button
  ctaButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.accent.primary,
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 24,
    gap: 8,
    marginBottom: 12,
  },
  ctaButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.text.primary,
  },

  // Skip Button
  skipButton: {
    alignItems: 'center',
    paddingVertical: 8,
  },
  skipButtonText: {
    fontSize: 13,
    color: Colors.text.tertiary,
    textDecorationLine: 'underline',
  },
});
